import AddIcon from "@mui/icons-material/Add";
import AutoGraphIcon from "@mui/icons-material/AutoGraph";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import TuneIcon from "@mui/icons-material/Tune";
import {
  Grid,
  Pagination,
  TableCell,
  TableFooter,
  TableRow,
  Typography,
} from "@mui/material";
import { uniq } from "lodash";
import moment from "moment";
import { useEffect, useState } from "react";
import { flushSync } from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import CustomButton from "../../../../components/CustomButton";
import CustomCheckbox from "../../../../components/CustomCheckbox";
import CustomModal from "../../../../components/CustomModal/CustomModal";
import CustomTextField from "../../../../components/CustomTextField";
import CustomSelect from "../../../../components/Select/Select";
import { getAllDiscountCodes } from "../../../../redux/reducers/clinicSlice";
import {
  getAllBillingRevenueReportData,
  getBillingRevenueReportData,
} from "../../../../redux/reducers/reportSlice";
import { AppColors } from "../../../../util/AppColors";
import { w20h20BlueIcon } from "../../../../util/CommonIconStyle";
import { PaymentTypeList } from "../../../../util/dropList";
import {
  getPetShopRecordBasedType,
  getPharmacyRecordBasedType,
  getTaxAndSubTotalAndTotal,
  getTreatmentRecordBasedType,
  roundWithSignedDiff,
} from "../../../../util/function";
import ReportTable from "../ReportTable";
import { nameExpan } from "../tableHeaders";

const tableHdrs = [
  "invoiceNumber",
  "invoiceDate",
  "paymentDate",
  "amtPaid",
  "paymentMode",
  "totalDue",
  "balanceDue",
  "discount",
  "discountCode",
  "taxes",
  "customerUserName",
  "petName",
];
const invoiceMap = [
  { key: "invoiceNumber", idType: "pharmacyDiscountId" },
  { key: "treatmentInvoiceNumber", idType: "treatmentDiscountId" },
  { key: "petShopInvoiceNumber", idType: "petShopDiscountId" },
];
const initSearch = {
  invoiceNumber: "",
  paymentMode: [],
  userName: "",
  petName: "",
};
const initFilter = {
  visible: false,
  added: false,
  selectedList: [],
  values: {},
};
const filterByList = [
  "Invoice Date",
  // "Collection Date",
  "Amount Paid",
  "Total Due",
  "Balance Due",
  "Discount",
  "Discount Code",
  "Taxes",
];
const dropFields = ["Discount Code"];
const splitRows = [
  "Invoice Date",
  // "Collection Date",
  "Amount Paid",
  "Total Due",
  "Balance Due",
  "Discount",
  "Taxes",
];

const BillingCollectionsTableReport = () => {
  const dispatch = useDispatch();
  const [crntTblHeders, setCrntTblHeders] = useState(tableHdrs?.slice(0, 5));
  const [activeTableHeaders, setActiveTableHeaders] = useState(
    tableHdrs?.slice(0, 5)?.map((pd, i) => ({ name: pd, isSelected: true, i })),
  );
  const [editColModVsble, setEditColModVsble] = useState(false);
  const [page, setPage] = useState(1);
  const [searchValues, setSearchValues] = useState(initSearch);
  const [filterValues, setFilterValues] = useState(initFilter);
  const { billingRevenue, billingRevenueAll } = useSelector(
    (state) => state?.report,
  );
  const { discountCodes } = useSelector((state) => state?.clinic);
  const rowsPerPage = 10;

  useEffect(() => {
    dispatch(getAllDiscountCodes());
    dispatch(getAllBillingRevenueReportData());
  }, [dispatch]);

  useEffect(() => {
    onApiCall();
  }, [
    searchValues?.invoiceNumber,
    searchValues?.paymentMode,
    searchValues?.userName,
    searchValues?.petName,
  ]);

  const getFilterObj = (currentPage = 1, reset = false) => {
    setPage(currentPage);
    const reqObj = { page: currentPage, itemSize: rowsPerPage };
    if (reset) return reqObj;
    if (searchValues?.invoiceNumber?.length > 0) {
      reqObj.invoiceNumber = [searchValues?.invoiceNumber];
    }
    if (searchValues?.paymentMode?.length > 0) {
      reqObj.paymentMode = searchValues?.paymentMode?.map((li) => li?.value);
    }
    if (searchValues?.userName?.length > 0) {
      reqObj.userName = [searchValues?.userName];
    }
    if (searchValues?.petName?.length > 0) {
      reqObj.petName = [searchValues?.petName];
    }

    if (filterValues?.selectedList?.includes("Invoice Date")) {
      reqObj.createdDateStart = moment(
        new Date(filterValues?.values?.["Start Invoice Date"] ?? new Date()),
      ).format("YYYY-MM-DD");
      reqObj.createdDateEnd = moment(
        new Date(filterValues?.values?.["End Invoice Date"] ?? new Date()),
      ).format("YYYY-MM-DD");
    }
    if (filterValues?.selectedList?.includes("Amount Paid")) {
      reqObj.minAmtPaid = Number(
        filterValues?.values?.["Min Amount Paid"] ?? 0,
      );
      reqObj.maxAmtPaid = Number(
        filterValues?.values?.["Max Amount Paid"] ?? 0,
      );
    }
    if (filterValues?.selectedList?.includes("Total Due")) {
      reqObj.minTotalDue = Number(filterValues?.values?.["Min Total Due"] ?? 0);
      reqObj.maxTotalDue = Number(filterValues?.values?.["Max Total Due"] ?? 0);
    }
    if (filterValues?.selectedList?.includes("Balance Due")) {
      reqObj.minBalanceDue = Number(
        filterValues?.values?.["Min Balance Due"] ?? 0,
      );
      reqObj.maxBalanceDue = Number(
        filterValues?.values?.["Max Balance Due"] ?? 0,
      );
    }
    if (filterValues?.selectedList?.includes("Discount")) {
      reqObj.minDiscount = Number(filterValues?.values?.["Min Discount"] ?? 0);
      reqObj.maxDiscount = Number(filterValues?.values?.["Max Discount"] ?? 0);
    }
    if (filterValues?.selectedList?.includes("Discount Code")) {
      reqObj.discountCode = filterValues?.values?.["Discount Code"]?.map(
        (li) => li?.value,
      );
    }
    if (filterValues?.selectedList?.includes("Taxes")) {
      reqObj.minTax = Number(filterValues?.values?.["Min Taxes"] ?? 0);
      reqObj.maxTax = Number(filterValues?.values?.["Max Taxes"] ?? 0);
    }
    return reqObj;
  };

  const onApiCall = (pge = 1, reset = false) => {
    dispatch(getBillingRevenueReportData(getFilterObj(pge, reset)));
  };

  const handleEditColumn = () => {
    const reqList = tableHdrs?.map((pd, i) => {
      const isObjMat = activeTableHeaders?.find(
        (tblHdr) => tblHdr?.name === pd,
      );
      return { name: pd, isSelected: isObjMat?.isSelected ?? false, i };
    });
    setActiveTableHeaders(reqList);
    setEditColModVsble(true);
  };

  const onEditModClose = () => {
    setEditColModVsble(false);
  };

  const handleSelcetdColumn = (selected, ind) => {
    const reqList = activeTableHeaders?.map((act, i) => {
      if (i === ind) return { ...act, isSelected: selected };
      return act;
    });
    setActiveTableHeaders(reqList);
  };

  const handleSaveTableColumn = () => {
    const reqList = activeTableHeaders
      ?.filter((act) => act?.isSelected)
      ?.map((act) => {
        return act?.name;
      });
    setCrntTblHeders(reqList);
    setEditColModVsble(false);
  };

  const getAllList = () => {
    const reqList = (billingRevenue?.data ?? [])?.flatMap((item) =>
      invoiceMap
        ?.filter(({ key }) => item[key]?.length > 0)
        ?.map(({ key, idType }) => {
          const billList =
            key === "treatmentInvoiceNumber"
              ? getTreatmentRecordBasedType(item?.paymentDetails)
              : key === "invoiceNumber"
                ? getPharmacyRecordBasedType(item?.paymentDetails)
                : key === "petShopInvoiceNumber"
                  ? getPetShopRecordBasedType(item?.paymentDetails)
                  : [];
          return {
            billList,
            invoiceNumber: item?.[key] ?? "",
            usedDicountCode: item?.[idType] ?? [],
            invoiceDate: item?.updatedDate ?? item?.createdDate,
            status: item?.status,
            amtPaid: item?.amtPaid,
            balanceDue: item?.balanceDue,
            customerUserName: item?.userName,
            petName: item?.petName,
            vetName: item?.doctorName,
            paymentMode: item?.paymentMode,
            type: item?.type,
            appointmentType:
              item?.appointmentType === "Home"
                ? "Home Visit"
                : item?.appointmentType,
          };
        }),
    );
    return reqList;
  };

  const totalPage = Math.ceil((getAllList()?.length ?? 0) / rowsPerPage);

  const handleChangePage = (e, selectedPage) => {
    setPage(selectedPage);
  };

  const getTableData = () => {
    return (getAllList() ?? [])
      ?.slice((page - 1) * rowsPerPage, page * rowsPerPage)
      ?.map((li) => {
        const reqDiscountCodes = (li?.usedDicountCode ?? [])?.map((nl) => {
          const reqCode = discountCodes?.find(
            (dc) => (dc?.billId ?? "")?.toString() === nl,
          );
          if (reqCode) return reqCode?.discountCode ?? "";
          return "";
        });
        const amtDetail = getTaxAndSubTotalAndTotal(li?.billList);
        const totalDue = Number(
          roundWithSignedDiff(amtDetail?.subTotal ?? 0)?.rounded,
        )?.toFixed(2);
        const discount = Number(
          roundWithSignedDiff(amtDetail?.discount ?? 0)?.rounded,
        )?.toFixed(2);
        const taxes = Number(
          roundWithSignedDiff(amtDetail?.tax ?? 0)?.rounded,
        )?.toFixed(2);
        return {
          ...li,
          totalDue,
          discount,
          taxes,
          discountCode:
            reqDiscountCodes?.length > 0 ? reqDiscountCodes?.join(", ") : null,
          invoiceDate: moment(new Date(li?.invoiceDate)).format("DD MMM YYYY"),
          paymentDate: moment(new Date(li?.invoiceDate)).format(
            "DD MMM YYYY",
          ),
          amtPaid: Number(li?.amtPaid)?.toFixed(2),
          balanceDue: Number(li?.balanceDue)?.toFixed(2),
        };
      });
  };

  const handleOpenChart = () => {
    console.log("Log triggered");
  };

  const onSearchChange = (e) => {
    const { name, value } = e?.target;
    setSearchValues({
      ...searchValues,
      [name?.trim()?.replace(/,$/, "")]: value, // remove spaces & trailing commas
    });
  };

  const onReset = () => {
    flushSync(() => {
      setSearchValues(initSearch);
    });
    flushSync(() => {
      setFilterValues({ ...initFilter, selectedList: [], values: {} });
    });
    onApiCall(1, true);
  };

  const onFilterChange = (name, value) => {
    const reqObj = { ...filterValues, [name]: value };
    setFilterValues(reqObj);
  };

  const getPetNames = () => {
    const reqList = (billingRevenueAll?.data ?? [])
      ?.map((li) => li?.petName)
      ?.filter(Boolean);
    const uniqList = uniq(reqList);
    return uniqList;
  };

  const getUserNames = () => {
    const reqList = (billingRevenueAll?.data ?? [])
      ?.map((li) => li?.userName)
      ?.filter(Boolean);
    const uniqList = uniq(reqList);
    return uniqList;
  };

  const onApplyFilter = () => {
    onApiCall();
    const reqObj = { ...filterValues, visible: false };
    setFilterValues(reqObj);
  };

  const getFieldName = (name, type) => {
    if (name === "Invoice Date") {
      if (type === "start") return "Start Invoice Date";
      return "End Invoice Date";
    }
    if (name === "Total Due") {
      if (type === "start") return "Min Total Due";
      return "Max Total Due";
    }
    if (name === "Amount Paid") {
      if (type === "start") return "Min Amount Paid";
      return "Max Amount Paid";
    }
    if (name === "Balance Due") {
      if (type === "start") return "Min Balance Due";
      return "Max Balance Due";
    }
    if (name === "Discount") {
      if (type === "start") return "Min Discount";
      return "Max Discount";
    }
    if (name === "Taxes") {
      if (type === "start") return "Min Taxes";
      return "Max Taxes";
    }
  };

  const getDiscountCodes = () => {
    const reqList = (billingRevenueAll?.data ?? [])
      ?.flatMap((li) => [
        ...(li?.petShopDiscountId ?? []),
        ...(li?.pharmacyDiscountId ?? []),
        ...(li?.treatmentDiscountId ?? []),
      ])
      ?.filter(Boolean);
    const uniqList = uniq(reqList);
    const reqDropList = uniqList?.map((li) => {
      const reqCode = (discountCodes ?? [])?.find(
        (dc) => (dc?.billId ?? "")?.toString() === li,
      );
      return {
        name: reqCode?.discountCode ?? "",
        value: reqCode?.discountCode ?? "",
      };
    });
    return reqDropList;
  };

  const getDropListByType = (typ) => {
    return typ === "Discount Code" ? getDiscountCodes() : [];
  };

  const onFilterValuesChange = (e) => {
    const { name, value } = e?.target;
    const reqObj = {
      ...filterValues,
      values: {
        ...filterValues?.values,
        [name?.trim()?.replace(/,$/, "")]: value,
      },
    };
    setFilterValues(reqObj);
  };

  const onSelectFilter = (name) => {
    const reqObj = { ...filterValues };
    let reqList = filterValues?.selectedList;
    if (reqList?.includes(name)) {
      reqList = reqList?.filter((item) => item !== name);
    } else {
      reqList.push(name);
    }
    reqObj.selectedList = reqList;
    setFilterValues(reqObj);
  };

  const onFilterAdd = (value) => {
    const reqObj = { ...filterValues, added: value };
    setFilterValues(reqObj);
  };

  const onFilterReset = () => {
    const reqObj = {
      ...initFilter,
      visible: true,
      selectedList: [],
      values: {},
    };
    setFilterValues(reqObj);
  };

  const onCloseFilter = () => {
    const reqObj = { ...filterValues, visible: false };
    setFilterValues(reqObj);
  };

  return (
    <>
      <div className="flex-row mv10">
        <div className="top-row-cen-con w100Per">
          <div className="w15Per">
            <CustomTextField
              options={[]}
              value={searchValues?.invoiceNumber}
              name="invoiceNumber"
              handleChange={onSearchChange}
              label="Invoice Number"
              backgroundColor={AppColors.lightBlue2}
              fullWidth
              suggetions
            />
          </div>
          <div className="w15Per ml20">
            <CustomSelect
              list={PaymentTypeList}
              value={searchValues?.paymentMode ?? []}
              name="paymentMode"
              handleChange={onSearchChange}
              label="Payment Mode"
              backgroundColor={AppColors.lightBlue2}
              multiSelectSearchCheck
            />
          </div>
          <div className="w15Per ml20">
            <CustomTextField
              options={getUserNames()}
              value={searchValues?.userName ?? []}
              name="userName"
              handleChange={onSearchChange}
              label="Customer Name"
              backgroundColor={AppColors.lightBlue2}
              fullWidth
              suggetions
            />
          </div>
          <div className="w15Per ml20">
            <CustomTextField
              options={getPetNames()}
              value={searchValues?.petName}
              name="petName"
              handleChange={onSearchChange}
              label="Pet Name"
              backgroundColor={AppColors.lightBlue2}
              fullWidth
              suggetions
            />
          </div>
          <div
            className="flex-row-ali-cen ml20 filter cursor"
            onClick={() => onFilterChange("visible", true)}
          >
            <TuneIcon sx={{ color: AppColors.appPrimary }} />
            <Typography className="font-medium fs12 blue-color ml10">
              {filterValues?.selectedList?.length > 0
                ? `${filterValues?.selectedList?.length} `
                : ""}
              Filter
            </Typography>
          </div>
          <div className="ml20">
            <CustomButton text="Reset" onClick={onReset} smallBtn redBtn />
          </div>
          <div
            className="flex-row-ali-cen ml20 cursor"
            onClick={handleEditColumn}
          >
            <EditOutlinedIcon sx={w20h20BlueIcon} />
            <Typography className="blue-color fs16 font-medium ml5">
              Edit columns ({crntTblHeders?.length})
            </Typography>
          </div>
          <div
            className="flex-row-ali-cen ml20 cursor"
            onClick={handleOpenChart}
          >
            <AutoGraphIcon sx={w20h20BlueIcon} />
          </div>
        </div>
      </div>
      <Typography className="font-medium fs12 gray28 ml30">
        Number of Records: {billingRevenue?.totalRecords ?? 0}
      </Typography>
      <div className="mtMin10">
        <ReportTable datas={getTableData()} columns={crntTblHeders} />
        <div className="flex1-end mtMin30">
          {Math.ceil((getAllList()?.length ?? 0) / rowsPerPage) > 1 ? (
            <TableFooter>
              <TableRow>
                <TableCell colSpan={12}>
                  <div className="flex1-end">
                    <Pagination
                      count={totalPage}
                      variant="outlined"
                      color="primary"
                      page={page}
                      onChange={handleChangePage}
                    />
                  </div>
                </TableCell>
              </TableRow>
            </TableFooter>
          ) : null}
        </div>
      </div>

      <CustomModal
        open={editColModVsble}
        onClose={onEditModClose}
        header="Columns"
        modal
        modalWidth={25}
      >
        <div className="ph20 scroll-70vh">
          {activeTableHeaders?.map((hr) => (
            <div className="flex-row" key={hr?.i}>
              <CustomCheckbox
                label={nameExpan?.[hr?.name]}
                checked={hr?.isSelected}
                onChange={() => handleSelcetdColumn(!hr?.isSelected, hr?.i)}
              />
            </div>
          ))}
          <div className="flex-end mb10">
            <div>
              <CustomButton
                text="Save"
                smallBtn
                onClick={handleSaveTableColumn}
              />
            </div>
          </div>
        </div>
      </CustomModal>
      <CustomModal
        open={filterValues?.visible}
        onClose={onCloseFilter}
        header="Filter"
        rightModal
        modalWidth={30}
      >
        <div className="scroll-80vh ph20">
          <Grid container spacing={2}>
            <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
              <Typography className="gray38 fs12 txt-regular">
                Filter by:
              </Typography>
              {!filterValues?.added ? (
                filterByList?.map((li, i) => (
                  <div className="flex-row-ali-cen" key={`${i}_li`}>
                    <div>
                      <CustomCheckbox
                        checked={filterValues?.selectedList?.includes(li)}
                        onChange={() => onSelectFilter(li)}
                      />
                    </div>
                    <Typography className="txt-regular fs14 black mb5 mlMin15">
                      {li}
                    </Typography>
                  </div>
                ))
              ) : (
                <div>
                  <div
                    className="flex-row-ali-cen gray-back4 ph10 pv10 br10 width-fit-con mt10 cursor"
                    onClick={() => onFilterAdd(false)}
                  >
                    <AddIcon
                      sx={{ color: AppColors.appPrimary, fontSize: 15 }}
                    />
                    <Typography className="font-medium fs12 blue-color ml5">
                      Add Filter
                    </Typography>
                  </div>
                  <div>
                    {filterValues?.selectedList?.map((li) => {
                      return (
                        <div className="mt20">
                          {dropFields?.includes(li) ? (
                            <CustomSelect
                              list={getDropListByType(li)}
                              value={filterValues?.values?.[li] ?? []}
                              name={li}
                              handleChange={onFilterValuesChange}
                              label={li}
                              multiSelectSearchCheck
                            />
                          ) : splitRows?.includes(li) ? (
                            <div className="flex-row-between-align-center">
                              <div className="w45Per">
                                <CustomTextField
                                  placeholder={getFieldName(li, "start")}
                                  fullWidth
                                  name={getFieldName(li, "start")}
                                  handleChange={onFilterValuesChange}
                                  value={
                                    filterValues?.values?.[
                                      getFieldName(li, "start")
                                    ] ?? ""
                                  }
                                  label={getFieldName(li, "start")}
                                  mobileDate={li === "Invoice Date"}
                                  type="number"
                                />
                              </div>
                              <div className="w45Per">
                                <CustomTextField
                                  placeholder={getFieldName(li, "end")}
                                  fullWidth
                                  name={getFieldName(li, "end")}
                                  handleChange={onFilterValuesChange}
                                  value={
                                    filterValues?.values?.[
                                      getFieldName(li, "end")
                                    ] ?? ""
                                  }
                                  label={getFieldName(li, "end")}
                                  mobileDate={li === "Invoice Date"}
                                  type="number"
                                />
                              </div>
                            </div>
                          ) : null}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              {!filterValues?.added ? (
                <div className="flex1-end mt15">
                  <div>
                    <CustomButton
                      text="Add"
                      smallBtn
                      onClick={() => onFilterAdd(true)}
                    />
                  </div>
                </div>
              ) : (
                <div className="flex-row-center mt15">
                  <div>
                    <CustomButton
                      text="Reset All"
                      smallBtn
                      onClick={onFilterReset}
                      tabSelectdBtn
                    />
                  </div>
                  <div className="ml20">
                    <CustomButton
                      text="Apply Filters"
                      smallBtn
                      onClick={onApplyFilter}
                    />
                  </div>
                </div>
              )}
            </Grid>
          </Grid>
        </div>
      </CustomModal>
    </>
  );
};

export default BillingCollectionsTableReport;
